/**
 * Shopify Variant Auto-Fallback
 * ==============================
 *
 * PURPOSE:
 * When a customer selects a new value for the first product option (e.g. Color),
 * this script automatically switches dependent options to the first available value
 * if the previously selected combination is no longer available (disabled).
 *
 * BACKGROUND:
 * This theme (Concept by RoarTheme) replaces the entire <variant-picker> element
 * via a fetch + viewTransition after each option change. The new HTML comes from
 * the server and already contains the correct disabled states. We therefore observe
 * the parent of <variant-picker> for newly inserted nodes and act immediately after
 * the new picker is injected into the DOM.
 *
 * HOW IT WORKS:
 * 1. On page load, find <variant-picker> and its parent container.
 * 2. Attach a MutationObserver to the parent, watching for new <variant-picker> nodes.
 * 3. When a new picker is inserted (after a user selects an option), check all
 *    dependent fieldsets (index 1+) for disabled selected options.
 * 4. If a selected option is disabled, click the first available option.
 *
 * INSTALLATION:
 * 1. Upload this file to your Shopify theme's `assets/` folder as `variant-fallback.js`
 * 2. Add the following line to `theme.liquid`, just before the closing </body> tag:
 *    {{ 'variant-fallback.js' | asset_url | script_tag }}
 *
 * DEBUG LOGGING:
 * All log messages use console.debug and are prefixed with [VFB].
 * To filter in Chrome DevTools: enable "Verbose" level and type [VFB] in filter box.
 */

(function () {

  /**
   * Logging helpers
   * ----------------
   * The theme overwrites console.log — we use console.debug instead.
   */
  const VFB = '[VFB]';
  const log   = (...args) => console.debug(VFB, ...args);
  const warn  = (...args) => console.debug('WARN',  VFB, ...args);
  const error = (...args) => console.debug('ERROR', VFB, ...args);

  /**
   * getOptionMap(picker)
   * ---------------------
   * Reads all radio inputs from the given picker element and returns
   * a structured array grouped by fieldset.
   */
  function getOptionMap(picker) {
    return [...picker.querySelectorAll('input[type="radio"][data-option-value-id]')].map(input => {
      const fieldset = input.closest('fieldset[data-option-index]');
      const fieldsetIndex = parseInt(fieldset?.dataset.optionIndex?.replace('option', '') ?? '1') - 1;
      return {
        value: input.value,
        disabled: input.disabled,
        checked: input.checked,
        fieldsetSlug: fieldset?.dataset.optionSlug,
        fieldsetIndex,
        element: input
      };
    });
  }

  /**
   * fixDependentOptions(picker)
   * ----------------------------
   * Called immediately after a new <variant-picker> is inserted into the DOM.
   * At this point the server-rendered HTML already has the correct disabled states.
   * We check each dependent fieldset (index 1+) and click the first available
   * option if the currently selected one is disabled.
   */
  function fixDependentOptions(picker) {
    log('fixDependentOptions() — reading fresh server-rendered disabled states...');

    const options = getOptionMap(picker);
    log('Option map:', options.map(o =>
      `[${o.fieldsetSlug}] "${o.value}" checked=${o.checked} disabled=${o.disabled}`
    ));

    // Group by fieldset index
    const byFieldset = {};
    options.forEach(o => {
      if (!byFieldset[o.fieldsetIndex]) byFieldset[o.fieldsetIndex] = [];
      byFieldset[o.fieldsetIndex].push(o);
    });

    const indices = Object.keys(byFieldset).map(Number).sort();
    log('Fieldset indices:', indices);

    // Only process dependent fieldsets (index 1+)
    for (let i = 1; i < indices.length; i++) {
      const group = byFieldset[indices[i]];
      const slug = group[0].fieldsetSlug;

      log(`--- Checking [${slug}] ---`);
      group.forEach(o => log(`  "${o.value}" checked=${o.checked} disabled=${o.disabled}`));

      const checked = group.find(o => o.checked);

      if (!checked) {
        warn(`  No checked option in [${slug}] — falling back.`);
      } else if (checked.disabled) {
        warn(`  "${checked.value}" is checked but DISABLED in [${slug}] — falling back.`);
      } else {
        log(`  "${checked.value}" is available — no action needed.`);
        continue;
      }

      const firstAvailable = group.find(o => !o.disabled);
      if (!firstAvailable) {
        error(`  No available option in [${slug}] — nothing to fall back to!`);
        continue;
      }

      log(`  Clicking first available: "${firstAvailable.value}" in [${slug}]`);
      firstAvailable.element.click();
      log(`  Done. [${slug}] set to "${firstAvailable.value}".`);
    }

    log('fixDependentOptions() complete.');
  }

  /**
   * observePickerParent(picker)
   * ----------------------------
   * Attaches a MutationObserver to the parent of <variant-picker>.
   * The theme's viewTransition inserts a new <variant-picker> as a previous
   * sibling before hiding and removing the old one. We detect this insertion
   * and immediately call fixDependentOptions on the new picker.
   */
  function observePickerParent(picker) {
    const parent = picker.parentElement;
    if (!parent) {
      warn('ABORT: <variant-picker> has no parent element.');
      return;
    }

    log('Attaching MutationObserver to parent of <variant-picker>:', parent.className || parent.tagName);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.tagName?.toLowerCase() === 'variant-picker') {
            log('New <variant-picker> detected in DOM — server HTML is ready.');
            fixDependentOptions(node);
            return;
          }
        }
      }
    });

    observer.observe(parent, { childList: true });
    log('MutationObserver active on picker parent.');
  }

  /**
   * init()
   * -------
   * Entry point. Finds the variant picker and starts observing its parent.
   */
  function init() {
    log('init() called...');

    const picker = document.querySelector('variant-picker');
    if (!picker) {
      warn('ABORT: No <variant-picker> found on this page.');
      return;
    }

    log('Found <variant-picker>:', picker.id);
    observePickerParent(picker);
    log('Init complete.');
  }

  if (document.readyState === 'loading') {
    log('Waiting for DOMContentLoaded...');
    document.addEventListener('DOMContentLoaded', init);
  } else {
    log('DOM ready — running init() immediately.');
    init();
  }

})();